import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Send, Eye, FlaskConical, X, Save, Clock, User, Users,
  Loader2, PenSquare, Trash2, ChevronDown, CalendarClock, Ban, Sparkles, Paperclip,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ── Quill config ──────────────────────────────────────────── */
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link', 'blockquote'];

const DARK_QUILL_CSS = `
  .tcl-quill .ql-toolbar{background:#0A1628;border:1px solid rgba(201,147,22,.12)!important;border-bottom:1px solid rgba(201,147,22,.06)!important;border-radius:8px 8px 0 0;padding:8px 10px;}
  .tcl-quill .ql-container{background:#070F20;border:1px solid rgba(201,147,22,.12)!important;border-top:none!important;border-radius:0 0 8px 8px;font-family:Georgia,'Times New Roman',serif;}
  .tcl-quill .ql-editor{color:#D4CCBC;font-family:Georgia,'Times New Roman',serif;font-size:15px;line-height:1.75;min-height:200px;padding:16px 20px;}
  .tcl-quill .ql-editor.ql-blank::before{color:rgba(212,204,188,.22);font-style:italic;font-family:Georgia,serif;}
  .tcl-quill .ql-editor p{margin:0 0 12px;}
  .tcl-quill .ql-editor h2{color:#E8E2D4;font-size:18px;margin:16px 0 8px;}
  .tcl-quill .ql-editor h3{color:#D4CCBC;font-size:16px;margin:12px 0 6px;}
  .tcl-quill .ql-editor a{color:#C99316;}
  .tcl-quill .ql-editor blockquote{border-left:3px solid rgba(201,147,22,.4);padding-left:14px;margin:12px 0;color:rgba(212,204,188,.65);font-style:italic;}
  .tcl-quill .ql-snow .ql-stroke{stroke:rgba(212,204,188,.4);}
  .tcl-quill .ql-snow .ql-fill{fill:rgba(212,204,188,.4);}
  .tcl-quill .ql-snow .ql-picker{color:rgba(212,204,188,.5);}
  .tcl-quill .ql-snow .ql-picker-options{background:#0A1628;border:1px solid rgba(201,147,22,.18);}
  .tcl-quill .ql-snow .ql-active .ql-stroke{stroke:#C99316;}
  .tcl-quill .ql-snow .ql-active .ql-fill{fill:#C99316;}
  .tcl-quill .ql-snow button:hover .ql-stroke{stroke:#C99316;}
  .tcl-quill .ql-snow .ql-picker-label:hover{color:#C99316;}
  .tcl-quill .ql-snow.ql-toolbar button:hover{background:rgba(201,147,22,.06);border-radius:4px;}
  .tcl-quill .ql-snow.ql-toolbar button.ql-active{background:rgba(201,147,22,.1);border-radius:4px;}
  .tcl-quill .ql-snow .ql-picker-label{padding-left:4px;}
`;

/* ── Helpers ───────────────────────────────────────────────── */
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

/* ── London timezone helpers ──────────────────────────────── */
/** Convert a datetime-local string (treated as Europe/London) → UTC ISO string */
const londonToUTC = (localStr) => {
  if (!localStr) return '';
  const [datePart, timePart] = localStr.split('T');
  const [y, mo, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  // Reference: what hour does London show at noon UTC on that day?
  const refUtc = new Date(Date.UTC(y, mo - 1, d, 12, 0));
  const londonNoon = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', hour12: false }).format(refUtc));
  const offsetHours = londonNoon - 12; // +1 BST, 0 GMT
  return new Date(Date.UTC(y, mo - 1, d, h - offsetHours, mi, 0)).toISOString();
};

/** Get current London offset label */
const getLondonOffsetLabel = () => {
  const d = new Date();
  const h = parseInt(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', hour12: false }).format(d));
  const offset = h - new Date().getUTCHours();
  return offset >= 1 ? 'BST (UTC+1)' : 'GMT (UTC+0)';
};

/** Default schedule value: tomorrow at 09:00 London time (datetime-local format) */
const defaultScheduleTime = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const londonStr = tomorrow.toLocaleDateString('en-CA', { timeZone: 'Europe/London' }); // YYYY-MM-DD
  return `${londonStr}T09:00`;
};

const FieldInput = ({ label, value, onChange, placeholder, testId, type = 'text' }) => (
  <div>
    <label className="text-cream/40 text-[11px] uppercase tracking-wider block mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full rounded-lg px-4 py-2.5 text-cream/80 text-sm placeholder-cream/20 focus:outline-none focus:border-gold/30 transition-colors"
      style={{ background: 'rgba(7,15,32,0.8)', border: '1px solid rgba(201,147,22,0.1)' }}
    />
  </div>
);

/* ══════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════ */
const AdminCompose = ({ isModal = false, onClose = null, initialData = null }) => {
  const { token } = useAuth();
  const hdrs = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  /* ── Form state ── */
  const [mode, setMode] = useState('direct');
  const [aliases, setAliases] = useState([]);
  const [bodyHtml, setBodyHtml] = useState('');
  const [form, setForm] = useState({
    to_email:   '',
    to_name:    '',
    cc:         '',
    bcc:        '',
    to_segment: 'all',
    from_alias: 'hello@thirdcultureleadership.co.uk',
    from_name:  'Third Culture Leadership',
    reply_to:   '',
    subject:    '',
    draft_id:   null,
  });

  /* ── UI state ── */
  const [sending, setSending]         = useState(false);
  const [testing, setTesting]         = useState(false);
  const [result, setResult]           = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [drafts, setDrafts]           = useState([]);
  const [showDrafts, setShowDrafts]   = useState(false);
  const [logs, setLogs]               = useState([]);
  const [showLog, setShowLog]         = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);
  const [lastSaved, setLastSaved]     = useState(null);
  const [hasUnsaved, setHasUnsaved]   = useState(false);
  const [relSavedTime, setRelSavedTime] = useState('');
  const [contacts, setContacts]       = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [showContacts, setShowContacts]   = useState(false);
  const [stats, setStats]             = useState(null);

  /* ── AI Draft state ── */
  const [aiDrafting, setAiDrafting]                     = useState(false);
  const [selectedNurtureStage, setSelectedNurtureStage] = useState(null);
  const [editorKey, setEditorKey]                       = useState(0);

  /* ── Attachments ── */
  const [attachments, setAttachments] = useState([]);
  const attachRef                     = useRef(null);

  /* ── Scheduling state ── */
  const [scheduleMode, setScheduleMode]   = useState(false);
  const [scheduledAt, setScheduledAt]     = useState(defaultScheduleTime());
  const [scheduled, setScheduled]         = useState([]);
  const [showScheduled, setShowScheduled] = useState(false);
  const [londonLabel]                     = useState(getLondonOffsetLabel);

  const draftTimer   = useRef(null);
  const contactTimer = useRef(null);
  const toInputRef   = useRef(null);
  const latestDraftRef = useRef({ form: null, bodyHtml: '', mode: 'direct' });

  /* ── Apply initialData when it changes ── */
  useEffect(() => {
    if (!initialData) return;
    setMode('direct');
    setBodyHtml(initialData.body_html || '');
    setForm(f => ({
      ...f,
      to_email:   initialData.to_email   || '',
      to_name:    initialData.to_name    || '',
      subject:    initialData.subject    || '',
      from_alias: initialData.from_alias || f.from_alias,
    }));
  }, [initialData]);

  /* ── Load aliases / drafts / log on mount ── */
  useEffect(() => {
    fetchAliases();
    fetchDrafts();
    fetchLogs();
    fetchScheduled();
    if (!isModal) fetchStats();
  }, []); // eslint-disable-line

  /* ── Auto-save draft every 30s ── */
  useEffect(() => {
    draftTimer.current = setInterval(() => {
      if (form.subject || bodyHtml) saveDraft(true);
    }, 30000);
    return () => clearInterval(draftTimer.current);
  }, [form, bodyHtml, mode]); // eslint-disable-line

  /* ── Track latest values for save-on-unmount ── */
  useEffect(() => {
    latestDraftRef.current = { form, bodyHtml, mode };
  }, [form, bodyHtml, mode]);

  /* ── Mark unsaved when content changes ── */
  useEffect(() => {
    if (form.subject || bodyHtml) setHasUnsaved(true);
  }, [form.subject, form.to_email, form.from_alias, form.subject, bodyHtml]); // eslint-disable-line

  /* ── Update relative saved time every 30s ── */
  useEffect(() => {
    if (!lastSaved) return;
    const update = () => {
      const diff = Math.floor((Date.now() - lastSaved.getTime()) / 60000);
      setRelSavedTime(diff < 1 ? 'just now' : diff + ' min ago');
    };
    update();
    const t = setInterval(update, 30000);
    return () => clearInterval(t);
  }, [lastSaved]);

  /* ── Contact autocomplete debounce ── */
  useEffect(() => {
    clearTimeout(contactTimer.current);
    contactTimer.current = setTimeout(() => searchContacts(contactSearch), contactSearch ? 320 : 0);
    return () => clearTimeout(contactTimer.current);
  }, [contactSearch]); // eslint-disable-line

  /* ── Sync from_name when alias changes ── */
  useEffect(() => {
    const alias = aliases.find(a => a.email === form.from_alias);
    if (alias) setForm(f => ({ ...f, from_name: alias.name }));
  }, [form.from_alias, aliases]);

  /* ── API helpers ── */
  const fetchAliases = async () => {
    try {
      const r = await fetch(`${API_URL}/api/compose/aliases`, { headers: hdrs() });
      if (r.ok) setAliases((await r.json()).aliases || []);
    } catch {}
  };

  const fetchDrafts = async () => {
    try {
      const r = await fetch(`${API_URL}/api/compose/drafts`, { headers: hdrs() });
      if (r.ok) setDrafts((await r.json()).drafts || []);
    } catch {}
  };

  const fetchLogs = async () => {
    try {
      const r = await fetch(`${API_URL}/api/compose/log`, { headers: hdrs() });
      if (r.ok) setLogs((await r.json()).logs || []);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const r = await fetch(`${API_URL}/api/cfq/admin/stats`, { headers: hdrs() });
      if (r.ok) setStats(await r.json());
    } catch {}
  };

  const searchContacts = async (q) => {
    try {
      const r = await fetch(`${API_URL}/api/compose/contacts?q=${encodeURIComponent(q)}&limit=8`, { headers: hdrs() });
      if (r.ok) setContacts((await r.json()).contacts || []);
    } catch {}
  };

  const fetchScheduled = async () => {
    try {
      const r = await fetch(`${API_URL}/api/compose/scheduled`, { headers: hdrs() });
      if (r.ok) setScheduled((await r.json()).scheduled || []);
    } catch {}
  };

  const cancelScheduled = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/compose/scheduled/${id}`, { method: 'DELETE', headers: hdrs() });
      if (r.ok) { toast.success('Scheduled send cancelled'); fetchScheduled(); }
      else toast.error('Could not cancel');
    } catch { toast.error('Network error'); }
  };

  /* ── Draft save ── */
  const saveDraft = async (silent = false) => {
    if (!form.subject && !bodyHtml) return;
    if (!silent) setDraftSaving(true);
    try {
      const payload = {
        id:         form.draft_id || undefined,
        mode,
        to_email:   form.to_email,
        to_name:    form.to_name,
        to_segment: form.to_segment,
        cc:         form.cc,
        bcc:        form.bcc,
        from_alias: form.from_alias,
        from_name:  form.from_name,
        reply_to:   form.reply_to,
        subject:    form.subject,
        body_html:  bodyHtml,
      };
      const r = await fetch(`${API_URL}/api/compose/draft`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify(payload),
      });
      if (r.ok) {
        const d = await r.json();
        if (!form.draft_id) setForm(f => ({ ...f, draft_id: d.id }));
        setLastSaved(new Date());
        setHasUnsaved(false);
        if (!silent) { toast.success('Draft saved'); fetchDrafts(); }
      }
    } catch {}
    if (!silent) setDraftSaving(false);
  };

  const loadDraft = (draft) => {
    setMode(draft.mode || 'direct');
    setBodyHtml(draft.body_html || '');
    setForm({
      to_email:   draft.to_email   || '',
      to_name:    draft.to_name    || '',
      cc:         draft.cc         || '',
      bcc:        draft.bcc        || '',
      to_segment: draft.to_segment || 'all',
      from_alias: draft.from_alias || 'hello@thirdcultureleadership.co.uk',
      from_name:  draft.from_name  || 'Third Culture Leadership',
      reply_to:   draft.reply_to   || '',
      subject:    draft.subject    || '',
      draft_id:   draft.id,
    });
    setShowDrafts(false);
    setResult(null);
    toast.success('Draft loaded');
  };

  const deleteDraft = async (id) => {
    try {
      await fetch(`${API_URL}/api/compose/draft/${id}`, { method: 'DELETE', headers: hdrs() });
      setDrafts(d => d.filter(x => x.id !== id));
      if (form.draft_id === id) setForm(f => ({ ...f, draft_id: null }));
    } catch {}
  };

  /* ── Close with auto-save ── */
  const handleClose = async () => {
    if ((form.subject || bodyHtml) && hasUnsaved) {
      await saveDraft(true);
    }
    if (onClose) onClose();
  };

  /* ── Preview ── */
  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const payload = {
        mode,
        to_email:   form.to_email || 'preview@example.com',
        to_name:    form.to_name,
        from_alias: form.from_alias,
        from_name:  form.from_name,
        subject:    form.subject || '(no subject)',
        body_html:  bodyHtml || '<p><em>No content yet.</em></p>',
        cc: [], bcc: [],
      };
      const r = await fetch(`${API_URL}/api/compose/preview-html`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify(payload),
      });
      if (r.ok) { setPreviewHtml((await r.json()).html || ''); setShowPreview(true); }
      else toast.error('Preview failed');
    } catch { toast.error('Preview failed'); }
    setPreviewLoading(false);
  };

  /* ── Test send ── */
  const handleTestEmail = async () => {
    setTesting(true); setResult(null);
    try {
      const r = await fetch(`${API_URL}/api/compose/test`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({ subject: `[TEST] ${form.subject || 'Test'}`, body: 'Test email delivery check.' }),
      });
      const d = await r.json();
      if (r.ok) { toast.success(d.message || 'Test sent to your inbox'); setResult({ success: true, message: d.message }); }
      else toast.error(d.detail || 'Test failed');
    } catch { toast.error('Network error'); }
    setTesting(false);
  };

  /* ── Send ── */
  const handleSend = async () => {
    if (!form.subject || !bodyHtml) return;
    setSending(true); setResult(null);

    try {
      if (mode === 'direct') {
        if (!form.to_email) { toast.error('Recipient email is required'); setSending(false); return; }
        const ccList  = form.cc  ? form.cc.split(',').map(s => s.trim()).filter(Boolean)  : [];
        const bccList = form.bcc ? form.bcc.split(',').map(s => s.trim()).filter(Boolean) : [];

        /* Try Hostinger first (1:1 mailbox threading) */
        let sent = false;
        try {
          const r = await fetch(`${API_URL}/api/hostinger-email/compose`, {
            method: 'POST', headers: hdrs(),
            body: JSON.stringify({
              to:           [form.to_email],
              cc:           ccList,
              bcc:          bccList,
              display_name: form.from_name,
              from_alias:   form.from_alias,
              from_name:    form.from_name,
              reply_to:     form.reply_to,
              subject:      form.subject,
              body_html:    bodyHtml,
            }),
          });
          if (r.ok) {
            sent = true;
            toast.success('Sent via Hostinger');
            setResult({ success: true, message: `Sent to ${form.to_email} via Hostinger` });
          }
          /* 503 = token not configured → fall through to Resend */
        } catch {}

        /* Fallback: Resend send-direct (branded HTML template) */
        if (!sent) {
          const r2 = await fetch(`${API_URL}/api/compose/send-direct`, {
            method: 'POST', headers: hdrs(),
            body: JSON.stringify({
              to_email:    form.to_email,
              to_name:     form.to_name,
              cc:          ccList,
              bcc:         bccList,
              from_alias:  form.from_alias,
              from_name:   form.from_name,
              reply_to:    form.reply_to,
              subject:     form.subject,
              body_html:   bodyHtml,
              draft_id:    form.draft_id,
              attachments: attachments.map(a => ({ filename: a.filename, content: a.content })),
            }),
          });
          const d2 = await r2.json();
          if (r2.ok) {
            sent = true;
            toast.success('Sent via Resend');
            setResult({ success: true, message: `Sent to ${form.to_email}` });
          } else {
            setResult({ success: false, error: d2.detail || 'Send failed' });
            toast.error(d2.detail || 'Send failed');
          }
        }

        if (sent) {
          if (form.draft_id) await deleteDraft(form.draft_id);
          fetchLogs();
          if (isModal && onClose) setTimeout(onClose, 1800);
        }

      } else {
        /* Mailing broadcast via Resend — immediate OR scheduled */
        if (scheduleMode) {
          /* ── Schedule for later ── */
          const utcIso = londonToUTC(scheduledAt);
          if (!utcIso) { toast.error('Please set a valid schedule time'); setSending(false); return; }
          const r = await fetch(`${API_URL}/api/compose/schedule`, {
            method: 'POST', headers: hdrs(),
            body: JSON.stringify({
              scheduled_at_utc: utcIso,
              to:               form.to_segment,
              subject:          form.subject,
              body_html:        bodyHtml,
              from_name:        form.from_name,
              from_alias:       form.from_alias,
              reply_to:         form.reply_to || form.from_alias,
              draft_id:         form.draft_id,
            }),
          });
          const d = await r.json();
          if (r.ok) {
            const londonDisplay = new Intl.DateTimeFormat('en-GB', {
              timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short',
            }).format(new Date(utcIso));
            toast.success(`Scheduled for ${londonDisplay} London time`);
            setResult({ success: true, message: `Scheduled for ${londonDisplay} (London)` });
            fetchScheduled();
            if (form.draft_id) setForm(f => ({ ...f, draft_id: null }));
          } else {
            setResult({ success: false, error: d.detail || 'Schedule failed' });
            toast.error(d.detail || 'Schedule failed');
          }
        } else {
          /* ── Send now ── */
          const r = await fetch(`${API_URL}/api/compose/send`, {
            method: 'POST', headers: hdrs(),
            body: JSON.stringify({
              to:         form.to_segment,
              subject:    form.subject,
              body:       '',
              body_html:  bodyHtml,
              from_name:  form.from_name,
              from_alias: form.from_alias,
              reply_to:   form.reply_to || form.from_alias,
            }),
          });
          const d = await r.json();
          if (r.ok) {
            toast.success(`Queued for ${d.recipient_count} recipients`);
            setResult({ success: true, message: `Queued for ${d.recipient_count} recipient(s). Sending in background…` });
            fetchLogs();
          } else {
            setResult({ success: false, error: d.detail || 'Send failed' });
            toast.error(d.detail || 'Send failed');
          }
        }
      }
    } catch { toast.error('Network error'); setResult({ success: false, error: 'Network error' }); }

    setSending(false);
  };

  /* ── AI Draft ── */
  const handleAiDraft = async () => {
    // In direct mode, require a recipient
    if (mode === 'direct' && !form.to_email) {
      toast.error('Enter a recipient email address first');
      return;
    }
    setAiDrafting(true);
    try {
      const isMailing = mode === 'mailing';
      const r = await fetch(`${API_URL}/api/compose/ai-draft`, {
        method: 'POST', headers: hdrs(),
        body: JSON.stringify({
          to_email:      isMailing ? 'subscriber@example.com' : form.to_email,
          to_name:       isMailing ? 'Valued Subscriber' : (form.to_name || ''),
          nurture_stage: selectedNurtureStage,
          from_alias:    form.from_alias,
          context:       isMailing
            ? `Broadcast email to the ${form.to_segment === 'all' ? 'full mailing list' : (form.to_segment + ' segment')}. Write an engaging broadcast message, not a personal 1:1 note.`
            : '',
        }),
      });
      const d = await r.json();
      if (r.ok) {
        if (d.subject && !form.subject) setForm(f => ({ ...f, subject: d.subject }));
        if (d.body_text) {
          const html = '<p>' + d.body_text.split('\n\n').join('</p><p>').split('\n').join('<br/>') + '</p>';
          setBodyHtml(html);
          setEditorKey(k => k + 1); // force ReactQuill remount so editor visually updates
        }
        toast.success('AI draft generated — review before sending');
      } else {
        toast.error(d.detail || 'AI draft failed');
      }
    } catch { toast.error('Network error'); }
    setAiDrafting(false);
  };

  /* ── File attachments ── */
  const handleAttachFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is too large (max 10 MB)`); return; }
      const reader = new FileReader();
      reader.onload = evt => {
        const base64 = evt.target.result.split(',')[1];
        setAttachments(prev => [...prev, { filename: file.name, content: base64, size: file.size }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  /* ── Sidebar toggling (only show one at a time) ── */
  const toggleDrafts    = () => { setShowDrafts(v => !v); setShowLog(false); setShowScheduled(false); };
  const toggleLog       = () => { setShowLog(v => !v); setShowDrafts(false); setShowScheduled(false); };
  const toggleScheduled = () => { setShowScheduled(v => !v); setShowDrafts(false); setShowLog(false); if (!showScheduled) fetchScheduled(); };

  const fromAlias = aliases.find(a => a.email === form.from_alias);

  /* ── Content ──────────────────────────────────────────────── */
  const content = (
    <>
      <style dangerouslySetInnerHTML={{ __html: DARK_QUILL_CSS }} />

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-4 pb-3" style={{ borderBottom: '1px solid rgba(201,147,22,0.12)' }}>
        <div className="flex items-center gap-3">
          <div style={{ width: '3px', height: '30px', background: 'rgba(201,147,22,0.85)', borderRadius: '0 2px 2px 0', flexShrink: 0 }} />
          <div>
            <h1 style={{ fontFamily: "'Crimson Pro', serif", color: 'rgba(250,248,243,0.92)', fontSize: '16px', fontWeight: 600, letterSpacing: '0.01em', lineHeight: 1.2 }}>New Message</h1>
            <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: 'rgba(250,248,243,0.28)', fontSize: '9.5px', letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: '1px' }}>Compose &amp; Send</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleDrafts} data-testid="compose-drafts-btn"
            className="relative flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-lg transition-all"
            style={{ color: 'rgba(212,204,188,0.4)', border: '1px solid rgba(212,204,188,0.07)', background: showDrafts ? 'rgba(201,147,22,0.06)' : 'transparent' }}>
            <Save size={11} /> Drafts
            {drafts.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                style={{ background: 'rgba(201,147,22,0.18)', color: '#C99316' }}>{drafts.length}</span>
            )}
          </button>
          <button onClick={toggleLog} data-testid="compose-log-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-lg transition-all"
            style={{ color: 'rgba(212,204,188,0.4)', border: '1px solid rgba(212,204,188,0.07)', background: showLog ? 'rgba(201,147,22,0.06)' : 'transparent' }}>
            <Clock size={11} /> Sent
          </button>
          {mode === 'mailing' && (
            <button onClick={toggleScheduled} data-testid="compose-scheduled-btn"
              className="relative flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-lg transition-all"
              style={{ color: 'rgba(212,204,188,0.4)', border: '1px solid rgba(212,204,188,0.07)', background: showScheduled ? 'rgba(201,147,22,0.06)' : 'transparent' }}>
              <CalendarClock size={11} /> Scheduled
              {scheduled.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold"
                  style={{ background: 'rgba(96,165,250,0.18)', color: '#60a5fa' }}>{scheduled.length}</span>
              )}
            </button>
          )}
          {isModal && onClose && (
            <button onClick={handleClose} data-testid="compose-close-btn"
              className="p-2 rounded-lg transition-all"
              style={{ color: 'rgba(212,204,188,0.4)' }}
              onMouseEnter={e => e.currentTarget.style.color='rgba(212,204,188,0.75)'}
              onMouseLeave={e => e.currentTarget.style.color='rgba(212,204,188,0.4)'}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ── Mode Toggle ── */}
      <div className="flex mb-5" style={{ borderBottom: '1px solid rgba(201,147,22,0.1)' }}>
        {[
          { id: 'direct',  icon: User,  label: 'Direct 1:1' },
          { id: 'mailing', icon: Users, label: 'Mailing List' },
        ].map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => setMode(id)}
            data-testid={"compose-mode-" + id}
            className="flex items-center gap-1.5 py-2.5 px-5 text-xs font-medium transition-all"
            style={{
              color:        mode === id ? '#C99316' : 'rgba(250,248,243,0.3)',
              borderBottom: '2px solid ' + (mode === id ? '#C99316' : 'transparent'),
              marginBottom: '-1px',
              fontFamily:   "'IBM Plex Sans', sans-serif",
              background:   mode === id ? 'rgba(201,147,22,0.04)' : 'transparent',
              letterSpacing: '0.04em',
            }}
          >
            <Icon size={11} /> {label}
          </button>
        ))}
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: (showDrafts || showLog || showScheduled) ? '1fr 280px' : '1fr' }}>
        {/* ── Left: Form ── */}
        <div className="space-y-4 min-w-0">

          {/* From alias */}
          <div>
            <label className="text-cream/40 text-[11px] uppercase tracking-wider block mb-1.5">From</label>
            <div className="relative">
              <select
                value={form.from_alias}
                onChange={e => setForm(f => ({ ...f, from_alias: e.target.value }))}
                data-testid="compose-from-alias"
                className="w-full rounded-lg px-4 py-2.5 text-cream/75 text-sm focus:outline-none appearance-none cursor-pointer pr-8"
                style={{ background: 'rgba(7,15,32,0.8)', border: '1px solid rgba(201,147,22,0.1)' }}
              >
                {aliases.length > 0
                  ? aliases.map(a => <option key={a.email} value={a.email} className="bg-gray-900">{a.name} &lt;{a.email}&gt;</option>)
                  : <option value="hello@thirdcultureleadership.co.uk" className="bg-gray-900">Third Culture Leadership &lt;hello@thirdcultureleadership.co.uk&gt;</option>
                }
              </select>
              <ChevronDown size={12} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(201,147,22,0.5)' }} />
            </div>
          </div>

          {/* Direct mode fields */}
          {mode === 'direct' && (
            <div className="space-y-3">
              {/* To with autocomplete */}
              <div className="relative">
                <label className="text-cream/40 text-[11px] uppercase tracking-wider block mb-1.5">To</label>
                <input
                  ref={toInputRef}
                  type="email"
                  value={form.to_email}
                  onChange={e => { setForm(f => ({ ...f, to_email: e.target.value })); setContactSearch(e.target.value); setShowContacts(true); }}
                  onFocus={() => { setContactSearch(form.to_email || ''); setShowContacts(true); }}
                  onBlur={() => setTimeout(() => setShowContacts(false), 180)}
                  placeholder="recipient@email.com — or type to search subscribers"
                  data-testid="compose-to-email"
                  className="w-full rounded-lg px-4 py-2.5 text-cream/80 text-sm placeholder-cream/20 focus:outline-none transition-colors"
                  style={{ background: 'rgba(7,15,32,0.8)', border: '1px solid rgba(201,147,22,0.1)' }}
                />
                {showContacts && contacts.length > 0 && (
                  <div className="absolute left-0 right-0 mt-1 rounded-lg shadow-2xl z-30 overflow-hidden"
                    style={{ background: '#071B3A', border: '1px solid rgba(201,147,22,0.15)', maxHeight: '240px', overflowY: 'auto' }}>
                    <div className="px-3 py-1.5" style={{ borderBottom: '1px solid rgba(201,147,22,0.08)', background: 'rgba(201,147,22,0.04)' }}>
                      <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: '9px', color: 'rgba(201,147,22,0.55)', textTransform: 'uppercase', letterSpacing: '0.18em' }}>
                        {contactSearch ? 'Matching subscribers' : 'Recent subscribers — click to prefill'}
                      </span>
                    </div>
                    {contacts.map(c => (
                      <button
                        key={c.email}
                        onMouseDown={() => {
                          setForm(f => ({ ...f, to_email: c.email, to_name: [c.first_name, c.last_name].filter(Boolean).join(' ') }));
                          setSelectedNurtureStage(c.nurture_stage ?? null);
                          setShowContacts(false); setContactSearch('');
                        }}
                        className="w-full text-left px-4 py-2.5 text-xs flex items-center gap-2.5 transition-colors"
                        style={{ color: 'rgba(212,204,188,0.65)' }}
                        onMouseEnter={e => e.currentTarget.style.background='rgba(201,147,22,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.background='transparent'}
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-bold"
                          style={{ background: c.confirmed ? 'rgba(20,184,166,0.15)' : 'rgba(100,116,139,0.15)', color: c.confirmed ? '#99F6E4' : '#94A3B8' }}>
                          {(c.first_name || c.email)[0].toUpperCase()}
                        </div>
                        <span className="font-medium text-cream/75">{c.first_name} {c.last_name}</span>
                        <span className="text-cream/30 truncate">{c.email}</span>
                        {c.confirmed && (
                          <span className="ml-auto text-[9px] flex-shrink-0" style={{ color: 'rgba(20,184,166,0.6)' }}>✓</span>
                        )}
                        {c.nurture_stage != null && (
                          <span className="text-[9px] flex-shrink-0" style={{ color: 'rgba(201,147,22,0.4)' }}>s{c.nurture_stage}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <FieldInput label="Name (optional)" value={form.to_name} onChange={v => setForm(f => ({ ...f, to_name: v }))} placeholder="Recipient's name" testId="compose-to-name" />

              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="CC" value={form.cc}  onChange={v => setForm(f => ({ ...f, cc: v }))}  placeholder="cc@email.com, …" testId="compose-cc" />
                <FieldInput label="BCC" value={form.bcc} onChange={v => setForm(f => ({ ...f, bcc: v }))} placeholder="bcc@email.com, …" testId="compose-bcc" />
              </div>
            </div>
          )}

          {/* Mailing segment selector */}
          {mode === 'mailing' && (
            <div className="space-y-3">
              <div>
                <label className="text-cream/40 text-[11px] uppercase tracking-wider block mb-1.5">To</label>
                <select
                  value={form.to_segment}
                  onChange={e => setForm(f => ({ ...f, to_segment: e.target.value }))}
                  data-testid="compose-to-segment"
                  className="w-full rounded-lg px-4 py-2.5 text-cream/75 text-sm focus:outline-none"
                  style={{ background: 'rgba(7,15,32,0.8)', border: '1px solid rgba(201,147,22,0.1)' }}
                >
                  <option value="all" className="bg-gray-900">All confirmed subscribers{stats ? ` (${stats.mailing_confirmed || 0})` : ''}</option>
                  <option value="tag:notify_sessions" className="bg-gray-900">Session-update subscribers</option>
                </select>
              </div>

              {/* Schedule toggle */}
              <div className="flex gap-1 p-1 rounded-lg" style={{ background: 'rgba(7,15,32,0.5)' }}>
                {[
                  { id: false, label: 'Send Now' },
                  { id: true,  label: 'Schedule for Later' },
                ].map(({ id, label }) => (
                  <button
                    key={String(id)}
                    onClick={() => setScheduleMode(id)}
                    data-testid={"compose-schedule-mode-" + (id ? 'later' : 'now')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md text-[11px] font-medium transition-all"
                    style={{
                      color:      scheduleMode === id ? '#C99316' : 'rgba(212,204,188,0.35)',
                      background: scheduleMode === id ? 'rgba(201,147,22,0.1)' : 'transparent',
                      border:     scheduleMode === id ? '1px solid rgba(201,147,22,0.2)' : '1px solid transparent',
                    }}
                  >
                    {id ? <CalendarClock size={11} /> : <Send size={11} />} {label}
                  </button>
                ))}
              </div>

              {/* Datetime picker (only when schedule mode) */}
              {scheduleMode && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-cream/40 text-[11px] uppercase tracking-wider">Send At (London time)</label>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(96,165,250,0.1)', color: 'rgba(96,165,250,0.65)', border: '1px solid rgba(96,165,250,0.15)' }}>
                      {londonLabel}
                    </span>
                  </div>
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={e => setScheduledAt(e.target.value)}
                    data-testid="compose-schedule-datetime"
                    className="w-full rounded-lg px-4 py-2.5 text-cream/75 text-sm focus:outline-none"
                    style={{ background: 'rgba(7,15,32,0.8)', border: '1px solid rgba(96,165,250,0.2)', colorScheme: 'dark' }}
                  />
                  <p className="text-[10px] mt-1.5" style={{ color: 'rgba(212,204,188,0.25)' }}>
                    Enter the time in <strong style={{ color: 'rgba(212,204,188,0.45)' }}>London time</strong>. The system will convert automatically. Cron runs hourly at :10.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Subject */}
          <FieldInput label="Subject" value={form.subject} onChange={v => setForm(f => ({ ...f, subject: v }))} placeholder="What's this about?" testId="compose-subject" />

          {/* Rich text body */}
          <div>
            <label className="text-cream/40 text-[11px] uppercase tracking-wider block mb-1.5">Message</label>
            <div className="tcl-quill" data-testid="compose-body-editor">
              <ReactQuill
                key={editorKey}
                value={bodyHtml}
                onChange={setBodyHtml}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Write your message…"
                theme="snow"
              />
            </div>
          </div>

          {/* Attachments area — direct mode only */}
          {mode === 'direct' && (
            <div>
              <input
                ref={attachRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleAttachFiles}
                accept="*/*"
                data-testid="compose-file-input"
              />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs"
                      style={{ background: 'rgba(59,130,246,0.07)', border: '1px solid rgba(59,130,246,0.18)', color: 'rgba(147,197,253,0.85)' }}>
                      <Paperclip size={10} />
                      <span className="truncate max-w-[140px]">{a.filename}</span>
                      <span className="opacity-50 text-[10px]">({(a.size / 1024).toFixed(0)} KB)</span>
                      <button
                        type="button"
                        onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                        className="ml-0.5 opacity-50 hover:opacity-100 transition-opacity"
                        data-testid={`compose-remove-attachment-${i}`}
                        title="Remove attachment"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            {/* AI Draft — available in BOTH direct and mailing modes */}
            <button
              onClick={handleAiDraft}
              data-testid="compose-ai-draft-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs transition-all"
              style={{
                color: aiDrafting ? 'rgba(201,147,22,0.45)' : '#C99316',
                border: '1px solid rgba(201,147,22,0.2)',
                background: 'rgba(201,147,22,0.08)',
                opacity: aiDrafting ? 0.7 : 1,
              }}
              title={mode === 'direct' && !form.to_email ? 'Enter a recipient first' : 'Generate AI draft based on nurture stage'}
            >
              {aiDrafting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {aiDrafting ? 'Generating…' : 'AI Draft'}
            </button>
            {mode === 'direct' && (
              <button
                type="button"
                onClick={() => attachRef.current?.click()}
                data-testid="compose-attach-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs transition-all"
                style={{ color: 'rgba(147,197,253,0.7)', border: '1px solid rgba(59,130,246,0.18)', background: 'rgba(59,130,246,0.06)' }}
                title="Attach files to this email">
                <Paperclip size={12} />
                {attachments.length > 0 ? `${attachments.length} attached` : 'Attach'}
              </button>
            )}
            <button onClick={handlePreview} disabled={previewLoading}
              data-testid="compose-preview-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs transition-all disabled:opacity-50"
              style={{ color: 'rgba(212,204,188,0.45)', border: '1px solid rgba(212,204,188,0.08)' }}>
              {previewLoading ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview
            </button>
            <button onClick={() => saveDraft(false)} disabled={draftSaving}
              data-testid="compose-save-draft-btn"
              className="relative flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs transition-all disabled:opacity-50"
              style={{
                color: hasUnsaved ? 'rgba(245,158,11,0.7)' : 'rgba(212,204,188,0.45)',
                border: hasUnsaved ? '1px solid rgba(245,158,11,0.25)' : '1px solid rgba(212,204,188,0.08)',
              }}>
              {draftSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {form.draft_id ? 'Update Draft' : 'Save Draft'}
              {hasUnsaved && <span className="w-1.5 h-1.5 rounded-full absolute -top-0.5 -right-0.5" style={{ background: 'rgba(245,158,11,0.85)' }} />}
            </button>
            <button onClick={handleTestEmail} disabled={testing || sending}
              data-testid="compose-test-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs transition-all disabled:opacity-50"
              style={{ color: 'rgba(201,147,22,0.55)', border: '1px solid rgba(201,147,22,0.15)' }}>
              <FlaskConical size={12} /> {testing ? 'Sending…' : 'Test Email'}
            </button>
            <button onClick={handleSend}
              disabled={sending || testing || !form.subject || !bodyHtml || (mode === 'direct' && !form.to_email)}
              data-testid="compose-send-btn"
              className="ml-auto flex items-center gap-2 px-6 py-2 rounded-lg text-xs font-semibold transition-all"
              style={{ background: (!sending && !testing && form.subject && bodyHtml && (mode !== 'direct' || form.to_email)) ? '#C99316' : 'rgba(201,147,22,0.3)', color: '#071B3A', opacity: (sending || testing || !form.subject || !bodyHtml || (mode === 'direct' && !form.to_email)) ? 0.5 : 1 }}>
              {scheduleMode && mode === 'mailing'
                ? <><CalendarClock size={12} /> {sending ? 'Scheduling…' : 'Schedule Send'}</>
                : <><Send size={12} /> {sending ? 'Sending…' : mode === 'direct' ? 'Send' : 'Send to List'}</>
              }
            </button>
          </div>

          {/* Status messages */}
          {(lastSaved || hasUnsaved) && (
            <div className="flex items-center gap-2">
              {hasUnsaved && (
                <span className="flex items-center gap-1 text-[10px]" style={{ color: 'rgba(245,158,11,0.55)' }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: 'rgba(245,158,11,0.7)' }} />
                  Unsaved changes
                </span>
              )}
              {lastSaved && !hasUnsaved && (
                <span className="text-[10px]" style={{ color: 'rgba(212,204,188,0.28)' }}>
                  Saved {relSavedTime}
                </span>
              )}
            </div>
          )}
          {result && (
            <div data-testid="compose-result"
              className="p-3 rounded-lg text-xs"
              style={{
                background: result.success ? 'rgba(52,211,153,0.08)' : 'rgba(239,68,68,0.08)',
                border:     result.success ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(239,68,68,0.2)',
                color:      result.success ? 'rgb(52,211,153)'                : 'rgb(252,165,165)',
              }}>
              {result.success ? result.message : result.error}
            </div>
          )}

          {/* Sending route info (compact) */}
          <div className="flex items-center gap-4 pt-1">
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: mode === 'direct' ? '#C99316' : 'rgba(212,204,188,0.2)' }} />
              <span className="text-[10px]" style={{ color: 'rgba(212,204,188,0.3)' }}>Direct → Hostinger / Resend</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: mode === 'mailing' ? '#C99316' : 'rgba(212,204,188,0.2)' }} />
              <span className="text-[10px]" style={{ color: 'rgba(212,204,188,0.3)' }}>Mailing → Resend</span>
            </div>
            {fromAlias && (
              <span className="text-[10px] font-mono ml-auto" style={{ color: 'rgba(201,147,22,0.4)' }}>{fromAlias.email.split('@')[0]}@</span>
            )}
          </div>
        </div>

        {/* ── Right: Drafts / Log / Scheduled sidebar ── */}
        {(showDrafts || showLog || showScheduled) && (
          <div className="rounded-xl p-4 self-start" style={{ background: 'rgba(7,27,58,0.5)', border: '1px solid rgba(201,147,22,0.07)', minWidth: 0 }}>
            {showDrafts && (
              <>
                <h3 className="text-[11px] uppercase tracking-wider font-semibold mb-3" style={{ color: 'rgba(212,204,188,0.4)' }}>Drafts</h3>
                {drafts.length === 0
                  ? <p className="text-xs text-center py-4" style={{ color: 'rgba(212,204,188,0.2)' }}>No drafts saved</p>
                  : <div className="space-y-2 max-h-72 overflow-y-auto">
                      {drafts.map(d => (
                        <div key={d.id} className="flex items-start gap-2 pb-2 border-b" style={{ borderColor: 'rgba(212,204,188,0.05)' }}>
                          <button onClick={() => loadDraft(d)} className="flex-1 text-left min-w-0">
                            <p className="text-xs truncate" style={{ color: 'rgba(212,204,188,0.6)' }}>{d.subject || '(no subject)'}</p>
                            <p className="text-[10px] mt-0.5" style={{ color: 'rgba(212,204,188,0.25)' }}>{d.mode} · {fmtTime(d.updated_at)}</p>
                          </button>
                          <button onClick={() => deleteDraft(d.id)} data-testid={"delete-draft-" + d.id}
                            className="flex-shrink-0 p-1 rounded"
                            style={{ color: 'rgba(239,68,68,0.4)' }}
                            onMouseEnter={e => e.currentTarget.style.color='rgba(239,68,68,0.75)'}
                            onMouseLeave={e => e.currentTarget.style.color='rgba(239,68,68,0.4)'}>
                            <Trash2 size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                }
              </>
            )}
            {showLog && (
              <>
                <h3 className="text-[11px] uppercase tracking-wider font-semibold mb-3" style={{ color: 'rgba(212,204,188,0.4)' }}>Sent Log</h3>
                {logs.length === 0
                  ? <p className="text-xs text-center py-4" style={{ color: 'rgba(212,204,188,0.2)' }}>No emails sent yet</p>
                  : <div className="space-y-2.5 max-h-72 overflow-y-auto">
                      {logs.map((log, i) => {
                        const statusColor = log.status === 'done' ? '#34d399' : log.status === 'sending' ? '#fbbf24' : log.status === 'failed' ? '#f87171' : '#60a5fa';
                        return (
                          <div key={i} className="pb-2 border-b" style={{ borderColor: 'rgba(212,204,188,0.05)' }}>
                            <div className="flex items-center justify-between gap-1">
                              <p className="text-xs truncate" style={{ color: 'rgba(212,204,188,0.55)' }}>{log.subject}</p>
                              <span className="text-[9px] font-semibold uppercase flex-shrink-0" style={{ color: statusColor }}>{log.status}</span>
                            </div>
                            <p className="text-[10px] mt-0.5" style={{ color: 'rgba(212,204,188,0.25)' }}>{log.to_filter || log.to_email} · {log.sent_count ?? log.recipient_count ?? '—'} sent</p>
                            <p className="text-[9px]" style={{ color: 'rgba(212,204,188,0.15)' }}>{fmtTime(log.sent_at)}</p>
                          </div>
                        );
                      })}
                    </div>
                }
              </>
            )}
            {showScheduled && (
              <>
                <h3 className="text-[11px] uppercase tracking-wider font-semibold mb-3" style={{ color: 'rgba(212,204,188,0.4)' }}>Scheduled Sends</h3>
                {scheduled.length === 0
                  ? <p className="text-xs text-center py-4" style={{ color: 'rgba(212,204,188,0.2)' }}>No pending scheduled sends</p>
                  : <div className="space-y-2.5 max-h-72 overflow-y-auto">
                      {scheduled.map(s => {
                        const sendAt = new Date(s.scheduled_at);
                        const londonDisplay = new Intl.DateTimeFormat('en-GB', {
                          timeZone: 'Europe/London', dateStyle: 'medium', timeStyle: 'short',
                        }).format(sendAt);
                        const isPast = sendAt <= new Date();
                        return (
                          <div key={s.id} className="pb-2 border-b" style={{ borderColor: 'rgba(212,204,188,0.05)' }}>
                            <div className="flex items-start gap-1.5">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs truncate font-medium" style={{ color: 'rgba(212,204,188,0.65)' }}>{s.subject}</p>
                                <p className="text-[10px] mt-0.5 flex items-center gap-1" style={{ color: isPast ? '#fbbf24' : 'rgba(96,165,250,0.65)' }}>
                                  <CalendarClock size={9} /> {londonDisplay} London
                                </p>
                                <p className="text-[10px]" style={{ color: 'rgba(212,204,188,0.2)' }}>{s.to_filter} · {s.status}</p>
                              </div>
                              {s.status === 'pending' && (
                                <button
                                  onClick={() => cancelScheduled(s.id)}
                                  data-testid={"cancel-scheduled-" + s.id}
                                  className="flex-shrink-0 p-1 rounded"
                                  title="Cancel scheduled send"
                                  style={{ color: 'rgba(239,68,68,0.4)' }}
                                  onMouseEnter={e => e.currentTarget.style.color='rgba(239,68,68,0.75)'}
                                  onMouseLeave={e => e.currentTarget.style.color='rgba(239,68,68,0.4)'}>
                                  <Ban size={11} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                }
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Preview Modal ── */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" style={{ maxWidth: '640px', width: '100%', maxHeight: '88vh' }}>
            <div className="px-6 py-4 flex items-center justify-between flex-shrink-0" style={{ background: '#071B3A' }}>
              <div>
                <h3 className="text-white font-semibold text-sm">Email Preview</h3>
                <p className="text-white/40 text-xs mt-0.5">
                  {mode === 'direct' ? 'Direct — no unsubscribe footer' : 'Mailing — full branded template'}
                </p>
              </div>
              <button onClick={() => setShowPreview(false)} data-testid="compose-preview-close"
                className="text-white/50 hover:text-white transition-colors p-1">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <iframe srcDoc={previewHtml} title="Email Preview" data-testid="compose-preview-iframe"
                className="w-full" style={{ height: '65vh', border: 'none', display: 'block' }} />
            </div>
          </div>
        </div>
      )}
    </>
  );

  /* ── Render ── */
  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.65)' }}
        onClick={handleClose}
        data-testid="compose-modal">
        <div
          className="w-full rounded-2xl shadow-2xl overflow-y-auto"
          style={{ maxWidth: '880px', maxHeight: '92vh', background: '#071B3A', border: '1px solid rgba(201,147,22,0.1)' }}
          onClick={e => e.stopPropagation()}>
          <div className="p-6 md:p-8">{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-compose">
      {content}
    </div>
  );
};

export default AdminCompose;
